import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  School,
  Camera,
  Video,
  Notebook,
  Palette,
  Building2,
  PenTool,
  CheckCircle2,
  ArrowRight,
  Newspaper,
  Globe,
  Trophy,
  FileText,
  Calendar,
  Star,
  Crown,
  Users,
  Award,
  Eye,
  Megaphone,
  Compass,
  Quote,
  Heart,
  Lightbulb,
  MapPin,
} from "lucide-react";
import PageShell from "@/components/PageShell";
import Button from "@/components/Button";
import Countdown from "@/components/Countdown";
import IndiaMap from "@/components/IndiaMap";
import { api } from "@/lib/api";
import { MEDIA, REGIONS } from "@/lib/media";

// ──────────────────────────────────────────────────────────────────────────────
// Reusable section wrapper
// ──────────────────────────────────────────────────────────────────────────────
function Section({ kicker, title, subtitle, children, className = "", testid }) {
  return (
    <section
      className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 ${className}`}
      data-testid={testid}
    >
      {kicker && (
        <p className="text-[11px] font-extrabold tracking-[0.25em] uppercase text-sprouts-secondary mb-2">
          {kicker}
        </p>
      )}
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-sprouts-text leading-tight">
        {title}
      </h2>
      {subtitle && (
        <p className="text-sm sm:text-base text-sprouts-muted mt-2.5 font-medium max-w-3xl">
          {subtitle}
        </p>
      )}
      <div className="mt-8">{children}</div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Data
// ──────────────────────────────────────────────────────────────────────────────
const PARTICIPATE = [
  { label: "Travel Story (Blog)", Icon: PenTool, color: "#3AB0FF" },
  { label: "Travel Photography", Icon: Camera, color: "#FF6B6B" },
  { label: "Travel Reel / Video", Icon: Video, color: "#FFD166" },
  { label: "Travel Journals", Icon: Notebook, color: "#34D399" },
  { label: "Photo Story / Artwork", Icon: Palette, color: "#A78BFA" },
  { label: "Participating Schools Across India", Icon: Building2, color: "#FB7185" },
];

const HOW_IT_WORKS = [
  {
    n: 1,
    title: "Schools Apply for Participation",
    desc: "Schools submit their participation request for the Sprouts Young Travel Storytelling Championship 2026.",
  },
  {
    n: 2,
    title: "Sprouts Reviews & Confirms Schools",
    desc: "Each registration is reviewed by the Sprouts team to keep the championship credible and fair.",
  },
  {
    n: 3,
    title: "Students Share Their Stories",
    desc: "Approved schools unlock student submissions — stories, photography, video and creative expression.",
  },
  {
    n: 4,
    title: "Champions Celebrated Regionally",
    desc: "Regional champions and category winners are recognised in September 2026 by Sprouts Edu Tours & Times NIE.",
  },
];

const WHY_PARTICIPATE = [
  { Icon: MapPin, text: "Represent your school and region on a national stage" },
  { Icon: Megaphone, text: "Share your travel experiences with a wider audience" },
  { Icon: Award, text: "Gain recognition from Sprouts Edu Tours & Times NIE" },
  { Icon: Camera, text: "Showcase creativity through stories, photography and video" },
  { Icon: Heart, text: "Inspire fellow students through your experiences" },
  { Icon: Crown, text: "Become a recognised young travel storyteller" },
];

const REGIONAL_CHAMPIONS = [
  "Telangana",
  "Andhra Pradesh",
  "Tamil Nadu",
  "Karnataka",
  "Maharashtra",
  "Delhi NCR",
  "Chandigarh & Tricity",
];

const CATEGORY_AWARDS = [
  { title: "Best Travel Story", color: "#3AB0FF" },
  { title: "Best Travel Photographer", color: "#FF6B6B" },
  { title: "Best Creativity", color: "#FFD166" },
  { title: "Most Inspiring Story", color: "#06B6D4" },
  { title: "Best School Participation", color: "#34D399" },
  { title: "Radio Mirchi Special", color: "#E14F4F" },
  { title: "Sprouts Special Recognition", color: "#A78BFA" },
  { title: "Participation Certificate", color: "#8B5CF6" },
];

const STORY_REACH = [
  { title: "Times NIE", sub: "India's premier student newspaper", Icon: Newspaper, color: "#3AB0FF" },
  { title: "Sprouts Edu Tours Platforms", sub: "Our website & social channels", Icon: Globe, color: "#FF6B6B" },
  { title: "Participating School Networks", sub: "Reaching schools nationwide", Icon: Building2, color: "#FFD166" },
  { title: "Digital Showcases", sub: "Featured online for the country to see", Icon: Eye, color: "#34D399" },
  { title: "Winner Recognition Ceremonies", sub: "Awards moments worth remembering", Icon: Award, color: "#A78BFA" },
];

const TESTIMONIALS = [
  {
    quote:
      "Travel taught me more in two weeks than a whole textbook ever could. The Championship gave me a stage to share those stories.",
    name: "Aanya, Grade 8",
    role: "Student",
    color: "#3AB0FF",
  },
  {
    quote:
      "Watching my child write about her trip — the people, the food, the smells — was the proudest learning moment we've had as a family.",
    name: "Mrs. R. Iyer",
    role: "Parent",
    color: "#FF6B6B",
  },
  {
    quote:
      "Our students returned from their tour transformed. This championship gives that transformation a voice and a national audience.",
    name: "Mr. S. Mehta, Principal",
    role: "Educator",
    color: "#FFD166",
  },
];

// ──────────────────────────────────────────────────────────────────────────────
// Small UI atoms
// ──────────────────────────────────────────────────────────────────────────────
function StatCard({ Icon, label, value, color, testid }) {
  return (
    <div
      className="flex-1 bg-white rounded-2xl p-5 border-2 shadow-card"
      style={{ borderColor: `${color}33` }}
      data-testid={testid}
    >
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
        style={{ backgroundColor: `${color}22` }}
      >
        <Icon size={22} color={color} />
      </div>
      <div className="text-3xl font-black text-sprouts-text tabular-nums">{value}</div>
      <div className="text-xs text-sprouts-muted font-bold mt-0.5">{label}</div>
    </div>
  );
}

function CredibilityStrip() {
  const items = [
    { Icon: Building2, text: "Schools across 10+ states" },
    { Icon: Users, text: "Thousands of student participants" },
    { Icon: Trophy, text: "Multiple regional & category awards" },
    { Icon: Newspaper, text: "Powered by Times NIE" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mt-8" data-testid="credibility-strip">
      {items.map(({ Icon, text }) => (
        <div
          key={text}
          className="flex items-center gap-2 bg-white/80 backdrop-blur border border-sprouts-border rounded-xl px-3 py-2.5"
        >
          <Icon size={16} className="text-sprouts-primary shrink-0" />
          <span className="text-xs font-extrabold text-sprouts-text leading-tight">{text}</span>
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────────
export default function Home() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api
      .stats()
      .then(setStats)
      .catch(() => setStats({ schools_participating: 0, entries_submitted: 0 }));
  }, []);

  return (
    <PageShell testid="home-screen">
      {/* ════════════════════════════════════════════════════════════════════
          1. HERO
          ════════════════════════════════════════════════════════════════════ */}
      <section
        className="relative overflow-hidden bg-gradient-to-br from-sprouts-lavender via-white to-sprouts-blush"
        data-testid="hero-section"
      >
        <IndiaMap
          className="absolute -right-32 -bottom-20 w-[680px] hidden lg:block pointer-events-none"
          color="#3AB0FF"
          opacity={0.07}
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Copy column */}
            <div className="lg:col-span-7 animate-fade-up">
              <div className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-soft mb-5">
                <Sparkles size={12} className="text-sprouts-secondary" />
                <span className="text-[11px] font-extrabold text-sprouts-secondary tracking-wide">
                  🌏 Sprouts Edu Tours · 2026
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.75rem] font-black leading-[1.05] text-sprouts-text tracking-tight" style={{ fontWeight: 900 }}>
                <span style={{ fontWeight: 900 }}>INDIA'S YOUNG</span>
                <br />
                <span style={{ fontWeight: 900 }}>TRAVEL STORYTELLING</span>
                <br />
                <span className="text-sprouts-secondary" style={{ fontWeight: 900 }}>CHAMPIONSHIP 2026</span>
              </h1>
              <p className="mt-5 text-base sm:text-lg text-sprouts-muted leading-relaxed max-w-2xl font-medium">
                In partnership with{" "}
                <span className="font-extrabold text-sprouts-text">Times NIE</span>,{" "}
                <span className="font-extrabold text-sprouts-text">Sprouts Edu Tours</span> invites
                students to transform their most memorable school journeys into stories, photographs,
                videos and creative expressions that inspire others.
              </p>

              {/* Participating regions strip */}
              <div className="mt-6" data-testid="regions-strip">
                <p className="text-[10px] font-extrabold tracking-[0.25em] uppercase text-sprouts-muted mb-2">
                  Participating Regions
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {REGIONS.map((r) => (
                    <span
                      key={r}
                      className="inline-flex items-center gap-1 bg-white border border-sprouts-border rounded-full px-2.5 py-1 text-[11px] font-bold text-sprouts-text"
                    >
                      <MapPin size={10} className="text-sprouts-primary" />
                      {r}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/register">
                  <Button data-testid="hero-cta-register">Register Your School</Button>
                </Link>
                <Link to="/submit">
                  <Button variant="secondary" data-testid="hero-cta-submit">
                    Submit Your Entry
                  </Button>
                </Link>
              </div>

              <CredibilityStrip />
            </div>

            {/* Single hero photo */}
            <div className="lg:col-span-5" data-testid="hero-photo">
              <div className="relative rounded-3xl overflow-hidden shadow-card">
                <img
                  src={MEDIA.hero}
                  alt="Sprouts students at Kennedy Space Center, NASA"
                  className="w-full h-[420px] sm:h-[500px] lg:h-[560px] object-cover object-top"
                  loading="eager"
                />
                <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full shadow-soft">
                    <Compass size={12} className="text-sprouts-primary" />
                    <span className="text-[11px] font-extrabold text-sprouts-text">
                      Real Sprouts students · Kennedy Space Center
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          2. LIVE STATS (real numbers)
          ════════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 -mt-8 sm:-mt-10 relative z-10">
        <div className="grid grid-cols-2 gap-4" data-testid="stats-row">
          <StatCard
            Icon={School}
            label="Schools Participating"
            value={stats?.schools_participating ?? "—"}
            color="#3AB0FF"
            testid="stat-schools"
          />
          <StatCard
            Icon={FileText}
            label="Entries Submitted"
            value={stats?.entries_submitted ?? "—"}
            color="#FF6B6B"
            testid="stat-entries"
          />
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          3. WHO CAN PARTICIPATE
          ════════════════════════════════════════════════════════════════════ */}
      <Section
        kicker="The Championship"
        title="Who Can Participate?"
        subtitle="Open to students sharing meaningful travel experiences from school trips with classmates and teachers — personal or family holiday stories are less preferred."
        testid="who-section"
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {PARTICIPATE.map(({ label, Icon, color }) => (
            <div
              key={label}
              className="bg-white rounded-2xl p-4 border border-sprouts-border shadow-soft hover:shadow-card transition-shadow"
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mb-2.5"
                style={{ backgroundColor: `${color}22` }}
              >
                <Icon size={26} color={color} />
              </div>
              <p className="font-extrabold text-sm text-sprouts-text leading-tight">{label}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ════════════════════════════════════════════════════════════════════
          4. HOW IT WORKS
          ════════════════════════════════════════════════════════════════════ */}
      <Section
        kicker="The Journey"
        title="How The Championship Works"
        subtitle="From school registration to national recognition — a transparent, four-step journey."
        testid="how-section"
      >
        <div className="overflow-x-auto -mx-4 sm:mx-0 pb-2">
          <div className="flex gap-4 px-4 sm:px-0 lg:grid lg:grid-cols-4 lg:gap-6">
            {HOW_IT_WORKS.map((s, idx) => (
              <div
                key={s.n}
                className="min-w-[260px] lg:min-w-0 bg-white rounded-2xl p-5 border border-sprouts-border shadow-card relative"
              >
                <div className="w-9 h-9 rounded-full bg-sprouts-primary flex items-center justify-center text-white font-black mb-3">
                  {s.n}
                </div>
                <h3 className="font-extrabold text-sprouts-text mb-1">{s.title}</h3>
                <p className="text-sm text-sprouts-muted leading-snug">{s.desc}</p>
                {idx < HOW_IT_WORKS.length - 1 && (
                  <div className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2">
                    <ArrowRight size={18} className="text-sprouts-primary" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ════════════════════════════════════════════════════════════════════
          5. WHY PARTICIPATE
          ════════════════════════════════════════════════════════════════════ */}
      <section className="bg-sprouts-slate text-white" data-testid="why-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <p className="text-[11px] font-extrabold tracking-[0.25em] uppercase text-sprouts-yellow mb-2">
            For Students & Schools
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black leading-tight">
            Why Participate?
          </h2>
          <p className="text-sm sm:text-base text-slate-300 mt-2.5 max-w-3xl">
            More than a competition — a chance to be heard, seen and celebrated as one of India's
            young travel storytellers.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {WHY_PARTICIPATE.map(({ Icon, text }, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-white/[0.06] backdrop-blur rounded-xl px-4 py-4 border border-white/10 hover:bg-white/[0.09] transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-sprouts-yellow/20 flex items-center justify-center shrink-0">
                  <Icon size={18} className="text-sprouts-yellow" />
                </div>
                <p className="text-sm font-semibold text-white leading-snug pt-1.5">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          6. RECOGNITION & AWARDS
          ════════════════════════════════════════════════════════════════════ */}
      <Section
        kicker="The Honours"
        title="Recognition & Awards"
        subtitle="Celebrating exceptional travel stories, creativity and student voices — region by region, category by category."
        testid="awards-section"
      >
        {/* Regional Champions — featured tier */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-5">
            <Crown size={20} className="text-sprouts-yellow" />
            <h3 className="text-lg sm:text-xl font-black text-sprouts-text tracking-wide">
              REGIONAL CHAMPIONS
            </h3>
            <span className="text-[10px] font-extrabold tracking-widest uppercase bg-sprouts-yellow/20 text-amber-700 px-2 py-1 rounded-full">
              Featured Tier
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {REGIONAL_CHAMPIONS.map((region) => (
              <div
                key={region}
                className="relative overflow-hidden bg-gradient-to-br from-sprouts-slate to-slate-800 text-white rounded-2xl p-5 sm:p-6 border border-sprouts-yellow/30 shadow-card"
                data-testid={`region-card-${region}`}
              >
                <Crown size={28} className="text-sprouts-yellow mb-3" />
                <p className="text-[11px] font-extrabold tracking-widest uppercase text-sprouts-yellow">
                  {region}
                </p>
                <p className="text-base sm:text-lg font-black mt-1 leading-tight">Champion</p>
                <p className="text-[11px] text-slate-300 font-bold mt-2">
                  Trophy • Certificate • Recognition • Cash Prize
                </p>
                <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-sprouts-yellow/5" />
              </div>
            ))}
          </div>
        </div>

        {/* Category Awards */}
        <div>
          <div className="flex items-center gap-2 mb-5">
            <Star size={18} className="text-sprouts-primary" />
            <h3 className="text-base sm:text-lg font-black text-sprouts-text tracking-wide uppercase">
              Category Awards
            </h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {CATEGORY_AWARDS.map((p) => (
              <div
                key={p.title}
                className="bg-white rounded-2xl p-5 border-2 flex flex-col items-center text-center shadow-soft hover:shadow-card transition-shadow"
                style={{ borderColor: `${p.color}55` }}
              >
                <Trophy size={44} color={p.color} className="mb-2" />
                <p className="font-extrabold text-sm text-sprouts-text mb-2 leading-tight">
                  {p.title}
                </p>
                <span
                  className="text-[10px] font-extrabold px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: `${p.color}22`, color: p.color }}
                >
                  Trophy • Certificate • Recognition • Cash Prize
                </span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ════════════════════════════════════════════════════════════════════
          7. WHERE COULD YOUR STORY BE SEEN
          ════════════════════════════════════════════════════════════════════ */}
      <Section
        kicker="The Reach"
        title="Where Could Your Story Be Seen?"
        subtitle="The best stories deserve to be seen. Selected entries may receive recognition and visibility through participating media and educational networks."
        testid="reach-section"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {STORY_REACH.map(({ title, sub, Icon, color }) => (
            <div
              key={title}
              data-testid={`reach-${title}`}
              className="bg-white rounded-2xl p-5 border-2 shadow-card hover:-translate-y-0.5 transition-transform"
              style={{ borderColor: `${color}55` }}
            >
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
                style={{ backgroundColor: `${color}22` }}
              >
                <Icon size={22} color={color} />
              </div>
              <p className="font-extrabold text-sprouts-text leading-tight">{title}</p>
              <p className="text-xs text-sprouts-muted mt-1 leading-snug">{sub}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ════════════════════════════════════════════════════════════════════
          8. CELEBRATING YOUNG STORYTELLERS (formerly Featured Winners)
          ════════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-2" data-testid="celebrating-section">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sprouts-primary via-sprouts-primary-dark to-sprouts-slate text-white p-8 sm:p-14">
          <IndiaMap
            className="absolute -right-20 -bottom-20 w-[520px]"
            color="#FFD166"
            opacity={0.1}
          />
          <div className="relative max-w-3xl">
            <div className="inline-flex items-center gap-1.5 bg-sprouts-yellow/20 text-sprouts-yellow px-3 py-1.5 rounded-full mb-5">
              <Trophy size={12} />
              <span className="text-[11px] font-extrabold tracking-widest uppercase">
                The Championship
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black leading-tight">
              🏆 Celebrating India's Young Travel Storytellers
            </h2>
            <p className="text-base sm:text-lg mt-4 text-white/85 leading-relaxed">
              Regional champions and category winners will be announced in{" "}
              <span className="font-black text-sprouts-yellow">September 2026</span>.
            </p>
            <p className="text-sm sm:text-base mt-3 text-white/75 leading-relaxed">
              Winning stories, photographs and videos will receive recognition, prizes and visibility
              through Sprouts Edu Tours and Times NIE — turning travel memories into journeys others
              can learn from.
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          9. WHY TRAVEL MATTERS
          ════════════════════════════════════════════════════════════════════ */}
      <Section
        kicker="The Philosophy"
        title="Travel Changes The Way We Learn"
        subtitle="Some lessons are learnt in classrooms. Others are discovered through journeys, experiences, cultures, conversations and exploration. This championship celebrates the stories that emerge from those journeys."
        testid="travel-matters-section"
      >
        <div className="grid lg:grid-cols-3 gap-5 mt-2">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={i}
              className="relative bg-white rounded-2xl p-6 border border-sprouts-border shadow-card"
              data-testid={`testimonial-${t.role.toLowerCase()}`}
            >
              <Quote
                size={36}
                className="absolute -top-4 left-5 text-white p-1.5 rounded-full"
                style={{ backgroundColor: t.color }}
              />
              <p className="text-sprouts-text text-base font-medium leading-relaxed pt-4">
                "{t.quote}"
              </p>
              <div className="mt-4 pt-4 border-t border-sprouts-border">
                <p className="font-extrabold text-sprouts-text text-sm">{t.name}</p>
                <p
                  className="text-[11px] font-extrabold tracking-widest uppercase mt-0.5"
                  style={{ color: t.color }}
                >
                  {t.role}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ════════════════════════════════════════════════════════════════════
          10. FINAL CTA
          ════════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-20">
        <div
          className="relative overflow-hidden bg-gradient-to-br from-sprouts-lavender to-sprouts-blush rounded-3xl p-8 sm:p-14 text-center"
          data-testid="final-cta"
        >
          <Sparkles className="absolute top-6 left-8 text-sprouts-yellow opacity-50" size={28} />
          <Sparkles className="absolute bottom-8 right-10 text-sprouts-secondary opacity-50" size={22} />
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-sprouts-text leading-tight">
            🌏 Every Journey Leaves A Story Behind.
          </h2>
          <p className="text-base sm:text-xl text-sprouts-muted mt-3 font-medium italic">
            Will yours be one of them?
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/register">
              <Button data-testid="final-cta-register">Register Your School</Button>
            </Link>
            <Link to="/submit">
              <Button variant="secondary" data-testid="final-cta-submit">
                Submit Your Entry
              </Button>
            </Link>
          </div>
          <div className="mt-7 flex justify-center">
            <Countdown testid="countdown-final" />
          </div>
        </div>
      </section>
    </PageShell>
  );
}
